import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Factory, Layers3, Package, Plus, UserRound } from 'lucide-react';
import type { MasterDataItem, Plant } from '@container-system/shared';
import { alloysApi, containersApi, driversApi, materialsApi, plantsApi } from '../../api/masterdata';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardBody } from '../../components/ui/Card';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { Input } from '../../components/ui/Input';
import { PageHeader } from '../../components/ui/PageHeader';
import { FullPageSpinner } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { ApiRequestError } from '../../api/client';

interface SimpleResource {
  list: () => Promise<MasterDataItem[]>;
  create: (input: { name: string }) => Promise<MasterDataItem>;
  update: (id: string, input: { name?: string; isActive?: boolean }) => Promise<MasterDataItem>;
  remove: (id: string) => Promise<null>;
}

const TABS = [
  { key: 'containers', label: 'Container', resource: containersApi, icon: Boxes },
  { key: 'materials', label: 'Materialien', resource: materialsApi, icon: Package },
  { key: 'alloys', label: 'Legierungen', resource: alloysApi, icon: Layers3 },
  { key: 'drivers', label: 'Fahrer', resource: driversApi, icon: UserRound },
] as const;

type TabKey = (typeof TABS)[number]['key'] | 'plants';

export function MasterDataPage() {
  const [tab, setTab] = useState<TabKey>('containers');

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Stammdaten" />

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <Button key={t.key} size="sm" variant={tab === t.key ? 'primary' : 'secondary'} onClick={() => setTab(t.key)}>
            <t.icon className="h-4 w-4" strokeWidth={1.75} />
            {t.label}
          </Button>
        ))}
        <Button size="sm" variant={tab === 'plants' ? 'primary' : 'secondary'} onClick={() => setTab('plants')}>
          <Factory className="h-4 w-4" strokeWidth={1.75} /> Werke
        </Button>
      </div>

      {tab === 'plants' ? <PlantsTab /> : <SimpleTab resource={TABS.find((t) => t.key === tab)!.resource} entityKey={tab} />}
    </div>
  );
}

function SimpleTab({ resource, entityKey }: { resource: SimpleResource; entityKey: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['master-data', entityKey], queryFn: resource.list });
  const [newName, setNewName] = useState('');

  const createMutation = useMutation({
    mutationFn: () => resource.create({ name: newName }),
    onSuccess: () => {
      setNewName('');
      void queryClient.invalidateQueries({ queryKey: ['master-data', entityKey] });
    },
    onError: (err) => toast.show(err instanceof ApiRequestError ? err.message : 'Anlegen fehlgeschlagen.', 'error'),
  });

  const toggleMutation = useMutation({
    mutationFn: (item: MasterDataItem) => resource.update(item.id, { isActive: !item.isActive }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['master-data', entityKey] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => resource.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['master-data', entityKey] }),
    onError: (err) => toast.show(err instanceof ApiRequestError ? err.message : 'Löschen fehlgeschlagen.', 'error'),
  });

  const columns: Column<MasterDataItem>[] = [
    { key: 'name', header: 'Name', render: (i) => i.name },
    { key: 'status', header: 'Status', render: (i) => (i.isActive ? <Badge tone="success">Aktiv</Badge> : <Badge tone="danger">Deaktiviert</Badge>) },
    {
      key: 'actions',
      header: '',
      render: (i) => (
        <div className="flex justify-end gap-2 text-xs">
          <button className="text-accent-400 hover:underline" onClick={() => toggleMutation.mutate(i)}>
            {i.isActive ? 'Deaktivieren' : 'Aktivieren'}
          </button>
          <button className="text-danger-500 hover:underline" onClick={() => deleteMutation.mutate(i.id)}>
            Löschen
          </button>
        </div>
      ),
    },
  ];

  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim()) createMutation.mutate();
          }}
        >
          <Input placeholder="Neuer Eintrag…" value={newName} onChange={(e) => setNewName(e.target.value)} className="max-w-xs" />
          <Button type="submit" size="sm" loading={createMutation.isPending}>
            <Plus className="h-4 w-4" strokeWidth={2} /> Hinzufügen
          </Button>
        </form>
        {query.isLoading ? <FullPageSpinner /> : <DataTable columns={columns} rows={query.data ?? []} rowKey={(i) => i.id} />}
      </CardBody>
    </Card>
  );
}

function PlantsTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['plants'], queryFn: plantsApi.list });
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const createMutation = useMutation({
    mutationFn: () => plantsApi.create({ name, code: code.toUpperCase() }),
    onSuccess: () => {
      setName('');
      setCode('');
      void queryClient.invalidateQueries({ queryKey: ['plants'] });
    },
    onError: (err) => toast.show(err instanceof ApiRequestError ? err.message : 'Anlegen fehlgeschlagen.', 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => plantsApi.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['plants'] }),
    onError: (err) => toast.show(err instanceof ApiRequestError ? err.message : 'Löschen fehlgeschlagen.', 'error'),
  });

  const columns: Column<Plant>[] = [
    { key: 'name', header: 'Name', render: (p) => p.name },
    { key: 'code', header: 'Code', render: (p) => <code className="rounded bg-surface-700 px-1.5 py-0.5 text-xs">{p.code}</code> },
    {
      key: 'actions',
      header: '',
      render: (p) => (
        <button className="text-xs text-danger-500 hover:underline" onClick={() => deleteMutation.mutate(p.id)}>
          Löschen
        </button>
      ),
    },
  ];

  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && code.trim()) createMutation.mutate();
          }}
        >
          <Input placeholder="Name (z.B. Werk 1)" value={name} onChange={(e) => setName(e.target.value)} className="max-w-xs" />
          <Input placeholder="Code (z.B. W1)" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="max-w-32" />
          <Button type="submit" size="sm" loading={createMutation.isPending}>
            <Plus className="h-4 w-4" strokeWidth={2} /> Hinzufügen
          </Button>
        </form>
        <p className="-mt-2 text-xs text-ivory-500">
          Hinweis: Die Codes <code className="rounded bg-surface-700 px-1 py-0.5">W1</code> und{' '}
          <code className="rounded bg-surface-700 px-1 py-0.5">W3</code> aktivieren bei der OCR-Erfassung die jeweils passende
          Felderkennung für die unterschiedlich aufgebauten Wiegescheine dieser Werke.
        </p>
        {query.isLoading ? <FullPageSpinner /> : <DataTable columns={columns} rows={query.data ?? []} rowKey={(p) => p.id} />}
      </CardBody>
    </Card>
  );
}
